// A Case's context on its screen: written live (the document editor's note
// field), together with every other window and with AI writes through MCP.
//
// Promise:
// - The field joins the context's live copy by the name the server gives
//   (`caseContextLiveQuery`). A new Case's field is written before the Case
//   exists and joins the copy once it is made, keeping what was written.
// - A refused connection asks for the name again: a new name (the copy was
//   rebuilt) starts a new field on it; the same name means this app is not the
//   server's editor version, and the field is drawn read only with a reload.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Editor } from "@tiptap/core";
import { CASE_TEXT_MAX } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import { useCallback, useState } from "react";
import { useApi } from "../app/context";
import type { LiveTransport } from "../documents/live";
import { LiveNoteField, NOTE_CLASS } from "../documents/live-note";
import { StaticBody } from "../documents/markdown-body";
import { caseContextLiveQuery } from "./queries";

const MIN_HEIGHT = 48;

export function CaseContext({
  caseId,
  note,
  transport,
  onEditor,
}: {
  /** null: a new Case, not made yet. */
  caseId: string | null;
  /** The stored context, drawn until the live copy has synced. */
  note: string;
  transport: LiveTransport;
  onEditor?(editor: Editor | null): void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const live = useQuery({
    ...caseContextLiveQuery(api, caseId ?? ""),
    enabled: caseId !== null,
  });
  // Bumped when the live copy was rebuilt under a new name: a fresh field.
  const [round, setRound] = useState(0);
  const [outdated, setOutdated] = useState(false);
  const onRefused = useCallback(
    async (name: string) => {
      if (caseId === null) return;
      const fresh = await qc
        .fetchQuery({ ...caseContextLiveQuery(api, caseId), staleTime: 0 })
        .catch(() => null);
      if (!fresh) return;
      if (fresh.name !== name) setRound((n) => n + 1);
      else setOutdated(true);
    },
    [qc, api, caseId],
  );
  if (outdated)
    return (
      <div style={{ minHeight: MIN_HEIGHT }}>
        <StaticBody markdown={note} className={NOTE_CLASS} />
        <p className="mt-1 flex items-center gap-2 text-[12px] text-muted">
          <span>{m.documents_reload_needed()}</span>
          <button
            type="button"
            className="cursor-pointer text-accent hover:underline"
            onClick={() => window.location.reload()}
          >
            {m.documents_reload()}
          </button>
        </p>
      </div>
    );
  return (
    <LiveNoteField
      key={round}
      transport={transport}
      liveName={live.data?.name ?? null}
      initialMarkdown={note}
      label={m.cases_context()}
      placeholder={m.cases_context_placeholder()}
      maxLength={CASE_TEXT_MAX}
      minHeight={MIN_HEIGHT}
      onEditor={onEditor}
      onRefused={onRefused}
    />
  );
}
